import React, { useState } from 'react';
import { useImageKitDelivery } from '../contexts/ImageKitConfigContext';
import {
  buildDeliverySrcSet,
  buildProxySrcSet,
  extractMediaStoragePath
} from '../utils/mediaImageUtils';
import { RESPONSIVE_IMAGE_VARIANTS } from '../utils/responsiveImageUtils';

function canDeliverViaLayer(src, endpointOrProxy) {
  if (!src || !endpointOrProxy) return false;
  return Boolean(extractMediaStoragePath(src));
}

/**
 * S3 masters delivered via ImageKit (when linked) or API proxy (resize/webp).
 * Falls back to raw S3 URL only if optimized delivery fails.
 */
export default function ResponsiveImage({
  src,
  alt = '',
  variant = 'card',
  sizes: sizesOverride,
  className,
  style,
  width,
  height,
  priority = false,
  loading: loadingProp,
  fetchPriority: fetchPriorityProp,
  decoding = 'async',
  onError,
  ...rest
}) {
  const { endpoint, deliveryMode, mediaDeliveryBase } = useImageKitDelivery();
  const [forceOrigin, setForceOrigin] = useState(false);

  const preset = RESPONSIVE_IMAGE_VARIANTS[variant] || RESPONSIVE_IMAGE_VARIANTS.card;
  const sizes = sizesOverride ?? preset.sizes;
  const loading = loadingProp ?? (priority ? 'eager' : 'lazy');
  const fetchPriority = fetchPriorityProp ?? (priority ? 'high' : 'low');

  const useImageKit =
    !forceOrigin &&
    deliveryMode === 'imagekit' &&
    endpoint &&
    canDeliverViaLayer(src, endpoint);

  const useProxy =
    !forceOrigin &&
    !useImageKit &&
    deliveryMode === 'proxy' &&
    mediaDeliveryBase &&
    canDeliverViaLayer(src, mediaDeliveryBase);

  const handleError = (event) => {
    if ((useImageKit || useProxy) && !forceOrigin) {
      setForceOrigin(true);
    }
    onError?.(event);
  };

  if (useImageKit) {
    const { srcSet, src: defaultSrc } = buildDeliverySrcSet(
      src,
      preset.widths,
      endpoint
    );
    return (
      <img
        {...rest}
        src={defaultSrc}
        srcSet={srcSet || undefined}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        className={className}
        style={style}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        onError={handleError}
      />
    );
  }

  if (useProxy) {
    const { srcSet, src: defaultSrc } = buildProxySrcSet(
      src,
      preset.widths,
      mediaDeliveryBase
    );
    return (
      <img
        {...rest}
        src={defaultSrc}
        srcSet={srcSet || undefined}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        className={className}
        style={style}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        onError={handleError}
      />
    );
  }

  return (
    <img
      {...rest}
      src={src}
      alt={alt}
      className={className}
      style={style}
      width={width}
      height={height}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding={decoding}
      onError={onError}
    />
  );
}
